import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { PageHeader } from "~/components/PageHeader";
import { useUserInquiries } from "~/lib/tanstack-query/hooks/useUserInquiries";
import { IMAGE_SIZES, buildImageUrl, cn } from "~/lib/utils";
import { useSetInquiryLocationMutation } from "~/lib/tanstack-query/hooks/mutations/inquiryLocations";
import { useUpdateInquiryStatusMutation } from "~/lib/tanstack-query/hooks/mutations/updateInquiryStatus";
import {
  openInquiry,
  setInquiryFormData,
} from "~/lib/stores/inquiry";
import { PaginationBar } from "~/components/ui/pagination-bar";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ArrowDownSmallIcon } from "~/icons/ArrowDownSmall";

export const Route = createFileRoute("/dashboard/inquiries")({
  component: RouteComponent,
});

type StatusFilter =
  | "all"
  | "open"
  | "pending"
  | "confirmed"
  | "declined"
  | "withdrawn";

type SortColumn = "date" | "status" | "name";
type SortDirection = "asc" | "desc";

type InquiryWithDetails = NonNullable<
  Awaited<
    ReturnType<
      typeof import("~/lib/server-functions/get-user-inquiries").getUserInquiries
    >
  >
>[number];

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  if (value == null || value === "") return null;
  return (
    <div className="grid grid-cols-[minmax(0,140px)_1fr] gap-2 py-1">
      <span className="text-black/70 text-sm">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function InquiryDetails({
  inquiry,
  formatDate,
  formatDateTime,
}: {
  inquiry: InquiryWithDetails;
  formatDate: (d: Date | null | undefined) => string;
  formatDateTime: (d: Date | null | undefined) => string;
}) {
  const appointments =
    typeof inquiry.appointments === "string" && inquiry.appointments
      ? (() => {
          try {
            const parsed = JSON.parse(inquiry.appointments) as Array<{
              start?: string;
              end?: string;
            }>;
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : [];

  const requestedRooms = Array.isArray(inquiry.requestedRooms)
    ? inquiry.requestedRooms
    : [];

  return (
    <div className="max-w-2xl space-y-4">
      <h4 className="font-semibold text-sm uppercase tracking-wide text-black/70">
        Anfrage-Details
      </h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-0">
          <DetailRow
            label="Angelegt"
            value={formatDateTime(inquiry.createdAt)}
          />
          <DetailRow label="Anrede" value={inquiry.salutation ?? undefined} />
          <DetailRow label="Vorname" value={inquiry.firstName} />
          <DetailRow label="Nachname" value={inquiry.lastName} />
          <DetailRow label="Unternehmen" value={inquiry.company ?? undefined} />
          <DetailRow label="E-Mail" value={inquiry.email} />
          <DetailRow label="Telefon" value={inquiry.telephone} />
        </div>
        <div className="space-y-0">
          <DetailRow label="Art" value={inquiry.businessType ?? undefined} />
          <DetailRow
            label="Event-Typ"
            value={inquiry.eventTypeName ?? undefined}
          />
          <DetailRow
            label="Anzahl Zimmer"
            value={inquiry.numberOfRooms ?? undefined}
          />
          <DetailRow
            label="Anzahl Gäste"
            value={inquiry.numberOfGuests ?? undefined}
          />
          <DetailRow
            label="Budget"
            value={
              inquiry.budget != null
                ? `${inquiry.budgetType === "Pro Person" ? "pro Person: " : ""}${inquiry.budget} ${inquiry.budgetCurrency ?? "EUR"}`
                : undefined
            }
          />
          {requestedRooms.length > 0 && (
            <div className="grid grid-cols-[minmax(0,140px)_1fr] gap-2 py-1">
              <span className="text-black/70 text-sm">Gewünschte Zimmer</span>
              <span className="text-sm">
                {requestedRooms.map((r) => r.name).join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>
      {appointments.length > 0 && (
        <div>
          <span className="text-black/70 text-sm block py-1">
            Terminvorschläge
          </span>
          <ul className="list-disc list-inside text-sm space-y-1">
            {appointments.map((a, i) => {
              const start = a.start ? new Date(a.start) : null;
              const end = a.end ? new Date(a.end) : null;
              const startStr =
                start && !Number.isNaN(start.getTime())
                  ? formatDateTime(start)
                  : "";
              const endStr =
                end && !Number.isNaN(end.getTime()) ? formatDateTime(end) : "";
              return (
                <li key={i}>
                  {startStr}
                  {startStr && endStr ? " – " : ""}
                  {endStr}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {"notes" in inquiry && inquiry.notes && (
        <DetailRow label="Anmerkungen" value={inquiry.notes} />
      )}
    </div>
  );
}

const INQUIRIES_PAGE_SIZE = 6;

function RouteComponent() {
  const { data: inquiries, isPending } = useUserInquiries();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortColumn, setSortColumn] = useState<SortColumn>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const updateInquiryStatusMutation = useUpdateInquiryStatusMutation();
  const setInquiryLocationMutation = useSetInquiryLocationMutation();

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (date: Date | null | undefined) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      open: "Offen",
      pending: "In Bearbeitung",
      confirmed: "Bestätigt",
      declined: "Abgelehnt",
      withdrawn: "Zurückgezogen",
    };
    return statusMap[status] || status;
  };

  const statusFilters: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "Alle" },
    { value: "open", label: "Offen" },
    { value: "pending", label: "In Bearbeitung" },
    { value: "confirmed", label: "Bestätigt" },
    { value: "declined", label: "Abgelehnt" },
    { value: "withdrawn", label: "Zurückgezogen" },
  ];

  const filteredInquiries =
    inquiries && statusFilter !== "all"
      ? inquiries.filter((inquiry) => inquiry.status === statusFilter)
      : (inquiries ?? []);

  const sortedInquiries = [...filteredInquiries].sort((a, b) => {
    const mult = sortDirection === "asc" ? 1 : -1;
    if (sortColumn === "date") {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return mult * (ta - tb);
    }
    if (sortColumn === "status") {
      const sa = a.status ?? "";
      const sb = b.status ?? "";
      return mult * sa.localeCompare(sb);
    }
    const na = (a.locationName ?? "").trim().toLowerCase();
    const nb = (b.locationName ?? "").trim().toLowerCase();
    return mult * na.localeCompare(nb);
  });

  const totalFiltered = sortedInquiries.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalFiltered / INQUIRIES_PAGE_SIZE),
  );
  const paginatedInquiries = sortedInquiries.slice(
    (currentPage - 1) * INQUIRIES_PAGE_SIZE,
    currentPage * INQUIRIES_PAGE_SIZE,
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection(column === "date" ? "desc" : "asc");
    }
    setCurrentPage(1);
  };

  const SortableHeader = ({
    column,
    label,
  }: {
    column: SortColumn;
    label: string;
  }) => (
    <th className="px-4 py-2 font-semibold">
      <button
        type="button"
        onClick={() => handleSort(column)}
        className="flex items-center gap-1 cursor-pointer hover:underline text-left"
      >
        {label}
        {sortColumn === column ? (
          <span
            className="inline-flex shrink-0 size-4 [&_svg]:size-full"
            style={{
              transform: sortDirection === "asc" ? "rotate(180deg)" : undefined,
            }}
          >
            <ArrowDownSmallIcon />
          </span>
        ) : null}
      </button>
    </th>
  );

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(1);
    setExpandedId(null);
  };

  return (
    <div className="w-full mb-6">
      <PageHeader title="Meine Anfragen" />
      <div className="flex flex-col gap-2">
        {/* Filter buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {statusFilters.map((filter) => (
            <button
              type="button"
              key={filter.value}
              className={cn(
                "cursor-pointer px-5 py-2 border-2 border-black",
                statusFilter === filter.value
                  ? "bg-black text-white"
                  : "bg-white text-black",
              )}
              onClick={() => handleStatusFilterChange(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {/* Table of user inquiries */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold w-12">&nbsp;</th>
                <SortableHeader column="date" label="Datum der Anfrage" />
                <SortableHeader column="status" label="Status" />
                <SortableHeader column="name" label="Name" />
                <th className="px-4 py-2 font-semibold">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td className="px-4 py-2 text-center" colSpan={5}>
                    Lädt...
                  </td>
                </tr>
              ) : paginatedInquiries.length > 0 ? (
                paginatedInquiries.map((inquiry) => {
                  const statusLabel = getStatusLabel(inquiry.status);
                  const isOpen = inquiry.status === "open";
                  const canReopen =
                    inquiry.status === "withdrawn" ||
                    inquiry.status === "confirmed";
                  const isExpanded = expandedId === inquiry.id;

                  const handleWithdraw = async () => {
                    await updateInquiryStatusMutation.mutateAsync({
                      inquiryId: inquiry.id,
                      status: "withdrawn",
                    });
                  };

                  const handleReopen = async () => {
                    if (inquiry.locationId == null) return;
                    await setInquiryLocationMutation.mutateAsync(
                      inquiry.locationId,
                    );
                    setInquiryFormData({
                      salutation: inquiry.salutation ?? undefined,
                      firstName: inquiry.firstName,
                      lastName: inquiry.lastName,
                      company: inquiry.company ?? undefined,
                      email: inquiry.email,
                      telephone: inquiry.telephone,
                      eventTypeId: inquiry.eventTypeId ?? undefined,
                      businessType: inquiry.businessType ?? undefined,
                      numberOfRooms: inquiry.numberOfRooms ?? undefined,
                      numberOfGuests: inquiry.numberOfGuests ?? undefined,
                      budgetType: inquiry.budgetType ?? undefined,
                      budget: inquiry.budget ?? undefined,
                      budgetCurrency: inquiry.budgetCurrency ?? undefined,
                      notes: inquiry.notes ?? undefined,
                      requestedRooms: Array.isArray(inquiry.requestedRooms)
                        ? inquiry.requestedRooms
                        : [],
                    });
                    openInquiry("direct", [inquiry.locationId]);
                  };

                  const toggleExpanded = () => {
                    setExpandedId((id) =>
                      id === inquiry.id ? null : inquiry.id,
                    );
                  };

                  return (
                    <Fragment key={inquiry.id}>
                      <tr className={cn(isExpanded && "border-b-0")}>
                        <td className="px-4 py-2 align-middle">
                          <button
                            type="button"
                            onClick={toggleExpanded}
                            className="inline-flex items-center justify-center p-1 rounded hover:bg-black/10 cursor-pointer"
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded
                                ? "Details schließen"
                                : "Details anzeigen"
                            }
                          >
                            {isExpanded ? (
                              <ChevronDown className="size-5" />
                            ) : (
                              <ChevronRight className="size-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          {formatDate(inquiry.createdAt)}
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <span className="font-bold">{statusLabel}</span>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <div className="flex items-center gap-3">
                            {"locationImagePath" in inquiry &&
                              inquiry.locationImagePath && (
                                <img
                                  src={buildImageUrl(inquiry.locationImagePath, {
                                    size: IMAGE_SIZES.cardThumb,
                                  })}
                                  alt={inquiry.locationName || ""}
                                  className="w-[72px] h-[77px] object-cover rounded"
                                />
                              )}
                            <div className="flex flex-col">
                              <span className="font-semibold">
                                {inquiry.locationName || "-"}
                              </span>
                              {"locationTypeName" in inquiry &&
                                inquiry.locationTypeName && (
                                  <span className="text-sm text-black">
                                    {inquiry.locationTypeName}
                                  </span>
                                )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          {isOpen && (
                            <button
                              type="button"
                              onClick={handleWithdraw}
                              disabled={updateInquiryStatusMutation.isPending}
                              className={cn(
                                "rounded-md w-full cursor-pointer px-4 py-2 border-2 border-black bg-white text-black",
                                "hover:bg-black hover:text-white",
                                "disabled:opacity-50 disabled:cursor-not-allowed",
                              )}
                            >
                              Zurückziehen
                            </button>
                          )}
                          {canReopen && (
                            <button
                              type="button"
                              onClick={handleReopen}
                              className={cn(
                                "rounded-md w-full cursor-pointer px-4 py-2 border-2 border-black bg-black text-white",
                                "hover:bg-black hover:text-white",
                                "disabled:opacity-50 disabled:cursor-not-allowed",
                              )}
                            >
                              Erneut anfragen
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-4 bg-black/5 align-top"
                          >
                            <InquiryDetails
                              inquiry={inquiry}
                              formatDate={formatDate}
                              formatDateTime={formatDateTime}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td className="px-4 py-2 text-center" colSpan={5}>
                    Keine Anfragen gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="mt-6"
          compact={false}
        />
      </div>
    </div>
  );
}
